/**
 * Tokens Store Slice
 *
 * Zustand slice for SMART Tokens state management (Organization-Level).
 * Handles organization token balance and Stripe checkout.
 *
 * NOTE: Transactions are only visible to org_admins on /organization/transactions page.
 *
 * GW-6533: `fetchTokenBalance` was removed because it had zero callers in
 * `src/`. The canonical org-level balance fetch is
 * `organizationTokensSlice.fetchOrganizationBalance`. Removing the dead
 * sibling method also removes a second `paymentsApi.getOrganizationTokenBalance`
 * call site that no longer needs the `assertOrgId` boundary.
 */

import { clearError as clearOpError, hasOperationLoaded, isOperationLoading } from '@gw/ui';

import type {
  ErrorStates,
  ErrorStatuses,
  LoadedTimestamps,
  LoadingStates,
  TokenBalance,
} from '@gw/ui/types';
import type { StateCreator } from 'zustand';

// ============================================================================
// Slice Interface
// ============================================================================

export interface TokensSlice {
  // State properties
  tokenBalance: TokenBalance | null;
  isLoading: LoadingStates;
  hasLoaded: LoadingStates;
  lastLoaded: LoadedTimestamps;
  errors: ErrorStates;
  errorStatuses: ErrorStatuses;

  // Utility actions
  clearError: (operation?: string) => void;
  checkIsLoading: (operation: string) => boolean;
  checkHasLoaded: (operation: string) => boolean;
}

// ============================================================================
// Slice Creator
// ============================================================================

export const createTokensSlice: StateCreator<TokensSlice> = (set, get) => ({
  // State initialization
  tokenBalance: null,
  isLoading: {},
  hasLoaded: {},
  lastLoaded: {},
  errors: {},
  errorStatuses: {},

  // Utility actions
  clearError: (operation?: string) => {
    const currentState = get();
    if (operation) {
      set({ errors: clearOpError(currentState.errors, operation) });
    } else {
      set({ errors: {} });
    }
  },
  checkIsLoading: (operation: string) => isOperationLoading(get().isLoading, operation),
  checkHasLoaded: (operation: string) => hasOperationLoaded(get().hasLoaded, operation),
});
